import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Alert, Badge, Button, Spinner } from './ui';
import { relTime } from '../lib/types';
import { UsageMeter, remainingShort } from '../lib/usage';

// One shared Deep Research surface (Phase 4). The panel (contact page) and the
// compact button (Call Tool / Lead Score rows) both call the SAME endpoint —
// POST /api/contacts/:id/research — no per-tool duplication.

const gradeColor = (g?: string) =>
  g === 'A' || g === 'B' ? 'border-success/50 bg-success/15 text-success'
  : g === 'C' ? 'border-victory/50 bg-victory/15 text-victory'
  : g ? 'border-danger/50 bg-danger/15 text-danger'
  : 'border-line text-muted';

interface Rubric { id: number; industryTag: string | null; version: number; currentVersion?: number; stale?: boolean }
interface ResearchData { brief: any; researchedAt: string | null; rubric?: Rubric | null; usage?: UsageMeter }

export function ResearchPanel({ contactId, onDone }: { contactId: number; onDone?: () => void }) {
  const { data, loading, setData } = useAsync<ResearchData>(() => apiGet(`/api/contacts/${contactId}/research`), [contactId]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limited, setLimited] = useState(false);
  const brief = data?.brief || null;
  const rubric = data?.rubric || null;
  const usage = data?.usage || null;

  async function run(force: boolean) {
    setRunning(true); setError(null); setLimited(false);
    try {
      const r = await apiPost<ResearchData>(`/api/contacts/${contactId}/research`, { force });
      setData({ brief: r.brief, researchedAt: r.researchedAt, rubric: r.rubric, usage: r.usage });
      onDone?.();
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        setLimited(true);
        setError(e.message);
        if (e.data?.usage) setData((d: ResearchData | null) => ({ ...(d || { brief: null, researchedAt: null }), usage: e.data.usage }));
      } else {
        setError(e instanceof ApiError ? e.message : 'Research failed.');
      }
    } finally { setRunning(false); }
  }

  if (loading) return null;

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-head text-sm font-semibold uppercase tracking-wide text-muted">Deep Research</span>
          {brief?.grade && <Badge className={gradeColor(brief.grade)}>Grade {brief.grade}</Badge>}
          {rubric && <Badge className="border-line text-muted">{rubric.industryTag || 'General'} rubric</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {usage && <span className="text-xs text-muted">{remainingShort(usage)}</span>}
          {data?.researchedAt && <span className="text-xs text-muted">{relTime(data.researchedAt)}</span>}
          <Button variant={brief ? 'ghost' : 'primary'} loading={running} disabled={usage ? usage.remaining <= 0 : false} onClick={() => run(!!brief)} className="px-3 py-1.5 text-sm">
            {brief ? 'Re-run' : 'Run Deep Research'}
          </Button>
        </div>
      </div>
      {rubric?.stale && !running && (
        <div className="mt-3"><Alert kind="info">This was researched with an older rubric (v{rubric.version}); the current one is v{rubric.currentVersion}. Re-run to grade against the updated criteria.</Alert></div>
      )}
      {running && <div className="mt-3 flex items-center gap-2 text-sm text-muted"><Spinner className="h-4 w-4" /> Researching the web against your rubric…</div>}
      {error && (
        <div className="mt-3">
          <Alert kind={limited ? 'info' : 'error'}>
            {error}
            {limited && <> <Link to="/usage" className="font-semibold underline">Manage usage / buy a top-up →</Link></>}
          </Alert>
        </div>
      )}
      {brief && !running && <BriefView brief={brief} />}
      {!brief && !running && !error && <p className="mt-3 text-sm text-muted">Web-research this business and grade it against your account's custom fit factors — what actually makes a good prospect for what you sell.</p>}
    </div>
  );
}

const assessColor = (a?: string) =>
  a === 'Strong' ? 'border-success/50 bg-success/10 text-success'
  : a === 'Moderate' ? 'border-electric/50 bg-electric/10 text-electric'
  : a === 'Weak' ? 'border-danger/50 bg-danger/10 text-danger'
  : 'border-line bg-surface-3 text-muted';

export function BriefView({ brief }: { brief: any }) {
  const factors: any[] = Array.isArray(brief.factors) ? brief.factors : [];
  return (
    <div className="mt-3 space-y-3 text-sm">
      {brief.gradeSummary && <p className="text-soft/90">{brief.gradeSummary}</p>}
      {brief.infoAvailability === 'limited' && (
        <div className="rounded-lg border border-victory/40 bg-victory/10 px-3 py-2 text-xs text-victory">
          Limited public information found — this is what we could confirm. The rest is worth verifying on the call.
        </div>
      )}

      {/* New rubric-driven shape: per-factor assessment + evidence. */}
      {factors.length > 0 ? (
        <div className="space-y-2">
          {factors.map((f, i) => (
            <div key={i} className="rounded-lg border border-line bg-surface-2 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-head font-semibold text-soft">{f.name}</span>
                <Badge className={assessColor(f.assessment)}>{f.assessment || 'Unknown'}</Badge>
              </div>
              {f.evidence && <p className="mt-1 text-soft/80">{f.evidence}</p>}
            </div>
          ))}
        </div>
      ) : (
        /* Backward-compat: render pre-Phase-5 briefs (fixed categories). */
        <BriefLegacy brief={brief} />
      )}

      {Array.isArray(brief.gaps) && brief.gaps.length > 0 && (
        <Section title="Gaps / unknowns to confirm"><ul className="list-disc space-y-0.5 pl-4 text-soft/90">{brief.gaps.map((g: string, i: number) => <li key={i}>{g}</li>)}</ul></Section>
      )}
      {Array.isArray(brief.talkingPoints) && brief.talkingPoints.length > 0 && (
        <Section title="Talking points"><ul className="list-disc space-y-0.5 pl-4 text-soft/90">{brief.talkingPoints.map((g: string, i: number) => <li key={i}>{g}</li>)}</ul></Section>
      )}
      {brief.nextStep && <Section title="Recommended next step"><p className="text-soft/90">{brief.nextStep}</p></Section>}
    </div>
  );
}

function BriefLegacy({ brief }: { brief: any }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {brief.website && (
          <div className="rounded-lg border border-line bg-surface-2 px-3 py-2"><div className="text-xs uppercase tracking-wide text-muted">Website</div><div className="text-soft">{brief.website.platform} · {brief.website.quality}</div></div>
        )}
        {brief.social?.google && (
          <div className="rounded-lg border border-line bg-surface-2 px-3 py-2"><div className="text-xs uppercase tracking-wide text-muted">Google</div><div className="text-soft">{brief.social.google.rating} ★ · {brief.social.google.count} reviews</div></div>
        )}
      </div>
      {brief.fit?.summary && <Section title="Fit"><p className="text-soft/90">{brief.fit.summary}</p></Section>}
      {Array.isArray(brief.questions) && brief.questions.length > 0 && (
        <Section title="Discovery questions"><ul className="list-disc space-y-0.5 pl-4 text-soft/90">{brief.questions.map((g: string, i: number) => <li key={i}>{g}</li>)}</ul></Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">{title}</div>
      {children}
    </div>
  );
}

// Compact entry point for tool rows (Call Tool, Lead Score). Runs the SAME shared
// endpoint, then links to the full contact page to read the brief.
export function ResearchButton({ contactId, className = '' }: { contactId: number; className?: string }) {
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error' | 'limited'>('idle');
  const navigate = useNavigate();
  async function run(e: React.MouseEvent) {
    e.stopPropagation(); e.preventDefault();
    setState('running');
    try { await apiPost(`/api/contacts/${contactId}/research`, {}); setState('done'); }
    catch (err) { setState(err instanceof ApiError && err.status === 402 ? 'limited' : 'error'); }
  }
  if (state === 'done') {
    return <button onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${contactId}`); }} className={`text-xs font-semibold text-success hover:underline ${className}`}>✓ Researched — view</button>;
  }
  if (state === 'limited') {
    return <button onClick={(e) => { e.stopPropagation(); navigate('/usage'); }} className={`text-xs font-semibold text-victory hover:underline ${className}`}>Limit reached — usage</button>;
  }
  return (
    <button onClick={run} disabled={state === 'running'} className={`text-xs font-semibold text-electric hover:underline disabled:opacity-50 ${className}`}>
      {state === 'running' ? 'Researching…' : state === 'error' ? 'Retry research' : '🔎 Research'}
    </button>
  );
}
